import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, RefreshCw, Users, EyeOff, Eye, ArrowRightLeft, SlidersHorizontal, Package, Tag, ChevronRight, Plus, MinusCircle, UserPlus } from "lucide-react";
import {
  AssetRecommendationListView,
  AssetRecommendationRowView,
  getAssetRecommendations,
  generateAssetRecommendations,
  skipAssetRecommendation,
  unskipAssetRecommendation,
  overrideRecommendationPriority,
} from "../../../../api/General/assets";
import { EquipmentPriority } from "../../../../api/General/businessRoles";
import { Spinner } from "@/components/ui/Spinner";
import Button from "@/components/ui/Button";
import { Switch } from "@/components/ui/Switch";
import { Tooltip } from "@/components/ui/Tooltip";
import { useToast, ToastRenderer } from "@/hooks/useToast";
import AssetRecommendationDrawer from "./AssetRecommendationDrawer";
import TransferRecommendationModal from "./TransferRecommendationModal";
import AddRequirementDialog from "./AddRequirementDialog";
import { parseApiUtc } from "@/utils/apiDate";
import { priorityToneClass, priorityLabelKey, priorityOrder } from '@/components/assets/equipmentRequirementChrome';

// Returns just the relative span; the caller wraps it in t("assetOverview.generatedAt").
//
// parseApiUtc, not `new Date`: the API sends UTC with no trailing Z, so a bare parse read it as local
// and reported a snapshot generated seconds ago as hours old, by exactly the viewer's UTC offset.
// Intl carries each language's own plural and relative-time rules, so this needs no locale keys.
function relativeSpan(iso: string, language: string): string {
  const generated = parseApiUtc(iso);
  if (!generated) return "";
  const rtf = new Intl.RelativeTimeFormat(language || "en", { numeric: "auto" });
  const mins = Math.min(0, Math.round((generated.getTime() - Date.now()) / 60000));
  if (mins > -1) return rtf.format(0, "minute");
  if (mins > -60) return rtf.format(mins, "minute");
  const hrs = Math.round(mins / 60);
  if (hrs > -24) return rtf.format(hrs, "hour");
  return rtf.format(Math.round(hrs / 24), "day");
}

interface Props {
  /** Refreshes the parent's Coverage tab badge: a cross off or an add changes the under-covered count,
      and the parent only refetched it on mount and after issue/return, so the badge went stale. */
  onCoverageChanged?: () => void;
  /** Category to open the drawer on, set when arriving from a Coverage drill-down. */
  focusCategoryId?: number | null;
  onFocusHandled?: () => void;
}

export default function AssetOverviewTab({ onCoverageChanged, focusCategoryId, onFocusHandled }: Props) {
  const { t, i18n } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [data, setData] = useState<AssetRecommendationListView | null>(null);
  const [loading, setLoading] = useState(true);
  const [addRequirementOpen, setAddRequirementOpen] = useState(false);
  const [error, setError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  // Held by id, not by object: a quiet reload replaces every row, and a captured row would go stale.
  const [drawerCategoryId, setDrawerCategoryId] = useState<number | null>(null);
  const [transferCategoryId, setTransferCategoryId] = useState<number | null>(null);
  const [priorityMenuFor, setPriorityMenuFor] = useState<number | null>(null);
  const [pendingCategoryId, setPendingCategoryId] = useState<number | null>(null);
  // Lifted out of the drawer so the pick survives closing it and reaches Transfer. Deliberately NOT
  // persisted: it only has meaning between opening the drawer and pressing Transfer.
  const [skuPicks, setSkuPicks] = useState<Record<string, number>>({});

  const selectedSkuFor = useCallback(
    (categoryId: number, employeeId: string) => skuPicks[`${categoryId}:${employeeId}`],
    [skuPicks]
  );

  const onSelectSku = useCallback((categoryId: number, employeeId: string, skuCategoryId: number) => {
    setSkuPicks((prev) => ({ ...prev, [`${categoryId}:${employeeId}`]: skuCategoryId }));
  }, []);

  // `quiet` keeps a row action from swapping the whole tab for one large spinner.
  const load = useCallback(async (includeHidden: boolean, quiet = false) => {
    // A quiet reload only ever follows a mutation, which is exactly when the parent badge is stale.
    if (quiet) onCoverageChanged?.();
    if (!quiet) setLoading(true);
    setError(false);
    try {
      const result = await getAssetRecommendations(includeHidden);
      if (result.errors.length === 0) setData(result.payload);
      else setError(true);
    } catch {
      setError(true);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [onCoverageChanged]);

  useEffect(() => {
    load(showHidden);
  }, [load, showHidden]);

  // The only thing in this tab that runs AI. Never fires on load.
  const handleGenerate = async () => {
    setGenerating(true);
    // A regenerated snapshot rebuilds the rows and their SKUs, so any pick against the old one is stale.
    setSkuPicks({});
    try {
      const result = await generateAssetRecommendations();
      if (result.errors.length === 0) await load(showHidden);
      else showToast(result.errors[0]?.description || t("assetOverview.generateError"), "error");
    } catch {
      showToast(t("assetOverview.generateError"), "error");
    } finally {
      setGenerating(false);
    }
  };

  const handleSkipToggle = async (row: AssetRecommendationRowView) => {
    if (pendingCategoryId !== null) return;
    setPendingCategoryId(row.categoryId);
    try {
      const result = row.isSkipped
        ? await unskipAssetRecommendation(row.categoryId)
        : await skipAssetRecommendation(row.categoryId);
      if (result.errors.length === 0) {
        showToast(row.isSkipped ? t("assetOverview.unskipped") : t("assetOverview.skipped"), "success");
        await load(showHidden, true);
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } finally {
      setPendingCategoryId(null);
    }
  };

  const handlePriority = async (categoryId: number, priority?: EquipmentPriority) => {
    if (pendingCategoryId !== null) return;
    setPriorityMenuFor(null);
    setPendingCategoryId(categoryId);
    try {
      const result = await overrideRecommendationPriority({ categoryId, priority });
      if (result.errors.length === 0) {
        showToast(t("assetOverview.priorityUpdated"), "success");
        await load(showHidden, true);
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } finally {
      setPendingCategoryId(null);
    }
  };

  const hasSnapshot = data?.hasSnapshot ?? false;
  const rows = data?.rows ?? [];

  // Open the drawer the Coverage drill-down asked for, once the rows it needs are actually loaded.
  // A category with no stock synthesises no row, so a missing target clears the request rather than
  // waiting on a drawer that can never open.
  useEffect(() => {
    if (focusCategoryId == null || loading) return;
    if (rows.some((r) => r.categoryId === focusCategoryId)) setDrawerCategoryId(focusCategoryId);
    onFocusHandled?.();
    // `rows` is rebuilt each render; `data` is the value that actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCategoryId, loading, data, onFocusHandled]);
  const drawerRow = rows.find((r) => r.categoryId === drawerCategoryId) ?? null;
  const transferRow = rows.find((r) => r.categoryId === transferCategoryId) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">{t("assetOverview.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("assetOverview.subtitle")}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-2">
          {hasSnapshot && (
            <label
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="overview-show-hidden"
            >
              <Switch checked={showHidden} onCheckedChange={setShowHidden} />
              {t("assetOverview.showHidden")}
              {(data?.hiddenCount ?? 0) > 0 && (
                <span className="text-xs">{t("assetOverview.hiddenCount", { count: data!.hiddenCount })}</span>
              )}
            </label>
          )}
          {/* The drawer can only extend a category the engine already surfaced. This is the way in
              for a requirement no role or goal declared. */}
          <Tooltip content={t("assetOverview.addRequirementTooltip")}>
            <Button size="sm" variant="outline" onClick={() => setAddRequirementOpen(true)} data-testid="overview-add-requirement">
              <Plus className="h-3.5 w-3.5 me-2" aria-hidden="true" />
              {t("assetOverview.addRequirement")}
            </Button>
          </Tooltip>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12" data-testid="overview-error">
          <p className="text-sm text-destructive">{t("assetOverview.loadError")}</p>
        </div>
      ) : !hasSnapshot ? (
        <div className="text-center py-12" data-testid="overview-gate">
          <Sparkles className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t("assetOverview.gateHeading")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("assetOverview.gateHint")}</p>
          <Button className="mt-4" onClick={handleGenerate} disabled={generating} data-testid="overview-generate">
            {generating ? <Spinner size="sm" className="me-2" /> : <Sparkles className="w-4 h-4 me-2" />}
            {generating ? t("assetOverview.generating") : t("assetOverview.generate")}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12" data-testid="overview-empty">
          <Users className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t("assetOverview.noRows")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("assetOverview.noRowsDescription")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            // Surfaced on the row itself: otherwise the only way to know a category has been
            // governed is to open the drawer, and the count beside it still reads as untouched.
            const crossedOff = row.employees.filter((e) => e.isWaived).length;
            const added = row.employees.filter((e) => e.isAddedByAssetManager).length;
            return (
            <div
              key={row.categoryId}
              className={`flex flex-col gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/30 sm:flex-row sm:items-center sm:justify-between ${
                row.isSkipped ? "opacity-60" : ""
              }`}
              data-testid={`overview-row-${row.categoryId}`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <Package className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-foreground">{row.categoryName}</p>
                    {row.isSkipped && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        <EyeOff className="h-3 w-3" />
                        {t("assetOverview.hiddenBadge")}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1 tabular-nums" data-testid={`overview-available-${row.categoryId}`}>
                      <Package className="h-3 w-3" />
                      {t("assetOverview.availableCount", { count: row.available })}
                    </span>
                    {row.skus.length > 0 && (
                      <span className="inline-flex min-w-0 items-center gap-1" data-testid={`overview-skus-${row.categoryId}`}>
                        <Tag className="h-3 w-3 shrink-0" />
                        <span className="truncate">{row.skus.map((s) => s.skuName).join(" · ")}</span>
                      </span>
                    )}
                    {crossedOff > 0 && (
                      <span className="inline-flex items-center gap-1 tabular-nums" data-testid={`overview-crossedoff-${row.categoryId}`}>
                        <MinusCircle className="h-3 w-3 shrink-0" />
                        {t("assetOverview.rowCrossedOff", { count: crossedOff })}
                      </span>
                    )}
                    {added > 0 && (
                      <span className="inline-flex items-center gap-1 tabular-nums" data-testid={`overview-added-${row.categoryId}`}>
                        <UserPlus className="h-3 w-3 shrink-0" />
                        {t("assetOverview.rowAdded", { count: added })}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <Tooltip content={t("assetOverview.priorityTooltip")}>
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${priorityToneClass[row.priority]}`}
                    data-testid={`overview-priority-${row.categoryId}`}
                  >
                    {t(priorityLabelKey[row.priority])}
                    {row.isPriorityOverridden && (
                      <span className="ms-1 text-[10px] opacity-70">{t("assetOverview.importanceOverridden")}</span>
                    )}
                  </span>
                </Tooltip>

                {/* The only way into cross off, restore and add, so it has to read as an action. */}
                <Tooltip content={t("assetOverview.viewEmployeesTooltip")}>
                  <button
                    type="button"
                    onClick={() => setDrawerCategoryId(row.categoryId)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                    data-testid={`overview-employees-${row.categoryId}`}
                  >
                    <Users className="h-3.5 w-3.5" />
                    {t("assetOverview.employeeCount", { count: row.employees.length })}
                    <ChevronRight className="h-3.5 w-3.5 rtl:rotate-180" />
                  </button>
                </Tooltip>

                <div className="relative">
                  <Tooltip content={t("assetOverview.importanceTooltip")}>
                    <button
                      type="button"
                      onClick={() => setPriorityMenuFor(priorityMenuFor === row.categoryId ? null : row.categoryId)}
                      disabled={pendingCategoryId === row.categoryId}
                      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50"
                      aria-expanded={priorityMenuFor === row.categoryId}
                      data-testid={`overview-importance-${row.categoryId}`}
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      {t("assetOverview.setImportance")}
                    </button>
                  </Tooltip>
                  {priorityMenuFor === row.categoryId && (
                    <div className="absolute z-20 mt-1 end-0 min-w-[10rem] rounded-md border border-border bg-popover p-1 shadow-md">
                      {priorityOrder.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => handlePriority(row.categoryId, p)}
                          className="block w-full rounded px-2 py-1.5 text-start text-xs text-popover-foreground hover:bg-muted"
                          data-testid={`overview-importance-${row.categoryId}-${p}`}
                        >
                          {t(priorityLabelKey[p])}
                        </button>
                      ))}
                      {row.isPriorityOverridden && (
                        <button
                          type="button"
                          onClick={() => handlePriority(row.categoryId, undefined)}
                          className="mt-1 block w-full rounded border-t border-border px-2 py-1.5 pt-1.5 text-start text-xs text-muted-foreground hover:bg-muted"
                          data-testid={`overview-importance-${row.categoryId}-clear`}
                        >
                          {t("assetOverview.clearOverride")}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <Tooltip content={t("assetOverview.transferTooltip")}>
                  <button
                    type="button"
                    onClick={() => setTransferCategoryId(row.categoryId)}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                    data-testid={`overview-transfer-${row.categoryId}`}
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5" />
                    {t("assetOverview.transfer")}
                  </button>
                </Tooltip>

                <Tooltip content={t("assetOverview.skipTooltip")}>
                  <button
                    type="button"
                    onClick={() => handleSkipToggle(row)}
                    disabled={pendingCategoryId === row.categoryId}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50"
                    data-testid={`overview-skip-${row.categoryId}`}
                  >
                    {pendingCategoryId === row.categoryId
                      ? <Spinner size="sm" />
                      : row.isSkipped ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    {row.isSkipped ? t("assetOverview.unskip") : t("assetOverview.skip")}
                  </button>
                </Tooltip>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {hasSnapshot && !loading && !error && (
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleGenerate} disabled={generating} data-testid="overview-refresh">
            {generating ? <Spinner size="sm" className="me-2" /> : <RefreshCw className="w-4 h-4 me-2" />}
            {generating ? t("assetOverview.generating") : t("assetOverview.refresh")}
          </Button>
          {data?.generatedOn && (
            <span className="text-xs text-muted-foreground" data-testid="overview-generated-at">
              {t("assetOverview.generatedAt", { time: relativeSpan(data.generatedOn, i18n.language) })}
            </span>
          )}
        </div>
      )}

      <AssetRecommendationDrawer
        row={drawerRow}
        onClose={() => setDrawerCategoryId(null)}
        onChanged={() => load(showHidden, true)}
        selectedSkuFor={selectedSkuFor}
        onSelectSku={onSelectSku}
      />
      <TransferRecommendationModal
        row={transferRow}
        selectedSkuFor={selectedSkuFor}
        onClose={() => setTransferCategoryId(null)}
        onTransferred={async (count) => {
          setTransferCategoryId(null);
          showToast(t("assetOverview.transferSuccess", { count }), "success");
          await load(showHidden, true);
        }}
      />
      <AddRequirementDialog
        open={addRequirementOpen}
        onClose={() => setAddRequirementOpen(false)}
        onCreated={() => load(showHidden, true)}
      />
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
